'use client'

import { useMemo, useState } from 'react'

import {
  ColumnDef,
  createColumnHelper,
  PaginationState,
} from '@tanstack/react-table'
import {
  DownloadIcon,
  ExternalLinkIcon,
  LoaderCircle,
  Trash2Icon,
} from 'lucide-react'

import { EMPTY_VALUE } from '~/app/accounting/display-utils'
import { reportKindLabel } from '~/app/accounting/reports/report-catalog'
import {
  AccountingReport,
  useAccountingReports,
  useDeleteAccountingReport,
} from '~/app/accounting/reports/useAccountingReports'
import { Table } from '~/components/Table'
import {
  TABLE_ACTION_COLUMN_META,
  TABLE_ACTION_COLUMN_WIDTH,
  TableActionTrigger,
} from '~/components/TableActions'
import { TruncateWithTooltip } from '~/components/TruncateWithTooltip'
import { Dropdown, DropdownEntry, Pill } from '~/components/component-library'
import { Timestamp } from '~/components/component-library/Timestamp'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { downloadFileFromUrl, openFileFromUrl } from '~/utils/files'

const INITIAL_PAGE_SIZE = 10

const COLUMN_WIDTHS: Record<string, number> = {
  kind: 260,
  format: 110,
  createdAt: 220,
  actions: TABLE_ACTION_COLUMN_WIDTH.size,
}

const columnHelper = createColumnHelper<AccountingReport>()

const downloadReport = (report: AccountingReport) => {
  if (!report.downloadUrl) return
  downloadFileFromUrl(report.downloadUrl, report.fileName)
}

const openReport = (report: AccountingReport) => {
  if (!report.downloadUrl) return
  openFileFromUrl(report.downloadUrl)
}

// Only a PDF renders in a browser tab; a CSV opens as an unreadable wall of
// commas, so it is offered as a download and nothing else.
const canOpenInTab = (report: AccountingReport) => report.format === 'pdf'

const activateReport = (report: AccountingReport) =>
  canOpenInTab(report) ? openReport(report) : downloadReport(report)

const StatusPill = ({ report }: { report: AccountingReport }) => {
  if (report.status === 'ready') return null
  if (report.status === 'failed') {
    return (
      <Pill
        size="small"
        variant="danger"
        label="Failed"
        tooltipMessage={report.failureReason ?? undefined}
      />
    )
  }
  return (
    <Pill
      size="small"
      label="Generating"
      icon={() => <LoaderCircle className="animate-spin" />}
    />
  )
}

export const reportActions = ({
  report,
  deleteReport,
}: {
  report: AccountingReport
  deleteReport: (reportId: string) => Promise<void>
}): DropdownEntry[] => {
  if (report.status === 'failed') {
    return [
      {
        content: 'Delete',
        icon: <Trash2Icon />,
        onClick: () => deleteReport(report.id),
      },
    ]
  }
  if (!report.downloadUrl) return []
  return [
    ...(canOpenInTab(report)
      ? [
          {
            content: 'Open in new tab',
            icon: <ExternalLinkIcon />,
            onClick: () => openReport(report),
          },
        ]
      : []),
    {
      content: 'Download',
      icon: <DownloadIcon />,
      onClick: () => downloadReport(report),
    },
  ]
}

const useReportColumns = () => {
  const [tz] = useAccountingTimezone()
  const deleteReport = useDeleteAccountingReport()

  return useMemo(() => {
    const unsized = [
      columnHelper.accessor('kind', {
        id: 'kind',
        header: 'Report',
        cell: ({ row, getValue }) => (
          <div className="flex items-center gap-2">
            <span>{reportKindLabel(getValue())}</span>
            <StatusPill report={row.original} />
          </div>
        ),
      }),
      // Unsized so it absorbs the remaining width; the filters line rides
      // along in the tooltip rather than taking a column of its own.
      columnHelper.accessor('periodLabel', {
        id: 'period',
        header: 'Period',
        cell: ({ row }) => (
          <TruncateWithTooltip
            content={row.original.filtersLabel ?? undefined}
            className="w-full min-w-0 overflow-hidden"
          >
            <span className="text-secondary">{row.original.periodLabel}</span>
          </TruncateWithTooltip>
        ),
      }),
      columnHelper.accessor('format', {
        id: 'format',
        header: 'Format',
        cell: ({ getValue }) => (
          <span className="uppercase text-secondary">{getValue()}</span>
        ),
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Generated',
        cell: ({ row }) => (
          <span className="text-secondary">
            <Timestamp
              timestamp={row.original.createdAt}
              type="relative"
              abbreviate
              tz={tz}
            />
            {row.original.createdByName
              ? ` by ${row.original.createdByName}`
              : ''}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        ...TABLE_ACTION_COLUMN_WIDTH,
        cell: ({ row }) => {
          const items = reportActions({ report: row.original, deleteReport })
          if (!items.length) {
            return <span className="text-secondary">{EMPTY_VALUE}</span>
          }
          return (
            <div
              className="flex justify-end"
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown items={items}>
                <TableActionTrigger aria-label="Report actions" />
              </Dropdown>
            </div>
          )
        },
        meta: TABLE_ACTION_COLUMN_META,
      }),
    ]

    return unsized.map((column) => ({
      ...column,
      size: COLUMN_WIDTHS[column.id!],
      minSize: COLUMN_WIDTHS[column.id!],
      ...(column.id !== 'period' && { maxSize: COLUMN_WIDTHS[column.id!] }),
    }))
  }, [tz, deleteReport])
}

export const ReportsTab = () => {
  const { reports, isLoading } = useAccountingReports()
  const columns = useReportColumns()

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: INITIAL_PAGE_SIZE,
  })

  return (
    <Table
      columns={columns as ColumnDef<AccountingReport>[]}
      data={reports}
      emptyMessage="No reports yet. Generate one to hand your accountant."
      // Rows still generating or failed have nothing to open.
      onRowClick={(row) => activateReport(row.original)}
      state={{ pagination }}
      onPaginationChange={setPagination}
      pagination={{
        enabled: true,
        initialPageSize: INITIAL_PAGE_SIZE,
      }}
      showColumnFilters={false}
      isLoading={isLoading}
    />
  )
}
